import { Image, Spin, Tooltip, Typography } from "antd";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SharepointApi } from "../types/sharepointApi";
import { GetAntIcon } from "../utils/ant_icons";
import fileTools from "../utils/fileTools";
import dayjs from "dayjs";

const { Text } = Typography;

type QuickAccessFile = {
	item_id: string;
	createdAt: string;
};

type QuickAccessFileMetaData = {
	name?: string;
	filetype?: string;
	thumbnail_url?: string;
	createdDateTime?: string;
};

type OwnerInfo = {
	item_id: string;
	owner_email?: string;
};

type SharepointQuickAccessProps = {
	api: SharepointApi;
	/** Whether "Edit in Microsoft 365" links may be created. */
	canCollaborate: boolean;
	files: QuickAccessFile[];
	fileMetaData: Record<string, QuickAccessFileMetaData>;
	ownerList: OwnerInfo[];
	containerId: string;
	tenantKey: string;
	collapsed: boolean;
	onToggleCollapse: () => void;
};

function SharepointQuickAccess(props: SharepointQuickAccessProps) {
	const {
		api,
		canCollaborate,
		files,
		fileMetaData,
		ownerList,
		containerId,
		tenantKey,
		collapsed,
		onToggleCollapse,
	} = props;

	const location = useLocation();

	const getOwner = (ownerList: OwnerInfo[], id: string): string => {
		return (
			ownerList.find((owner: OwnerInfo) => owner.item_id === id)?.owner_email ||
			"-"
		);
	};

	const handleFileClick = (fileId: string, filetype?: string) => {
		if (!containerId) return;

		if (
			canCollaborate &&
			filetype &&
			fileTools.checkSharepointEdittable(filetype)
		) {
			api.collaborate(fileId, containerId).then((response: any) => {
				window.open(response?.link.webUrl, "__blank");
			});
		} else {
			window.open(
				api.previewPath(fileId, containerId, location.pathname),
				"_blank"
			);
		}
	};

	const quickAccessItems = useMemo(() => {
		return files.map((file: QuickAccessFile) => {
			const meta = fileMetaData[file.item_id];
			let filename = meta?.name;
			const lastDotIndex = filename?.lastIndexOf(".");

			if (lastDotIndex != null && lastDotIndex !== -1) {
				filename = filename?.substring(0, lastDotIndex);
			}

			return (
				<Spin key={file.item_id} spinning={!meta}>
					<div
						className="quick-access-item"
						style={{
							cursor: "pointer",
							width: "260px",
							height: "185px",
							padding: "20px",
							gap: "15px",
							display: "flex",
							flexDirection: "column",
						}}
						onClick={() => handleFileClick(file.item_id, meta?.filetype)}
					>
						<Image
							className="no-visual-search"
							preview={false}
							height={80}
							width={220}
							src={meta?.thumbnail_url || "fallback-image-url"}
						/>
						<div
							style={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								justifyContent: "space-between",
								width: "100%",
							}}
						>
							<Text
								style={{
									color: "inherit",
									fontWeight: "700",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								ellipsis={{
									tooltip: <span>{filename}</span>,
								}}
							>
								{filename}
							</Text>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									flexWrap: "wrap",
									width: "100%",
								}}
							>
								<Text
									style={{
										color: "inherit",
										opacity: "0.6",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
									ellipsis={{
										tooltip: <span>{getOwner(ownerList, file.item_id)}</span>,
									}}
								>
									{getOwner(ownerList, file.item_id)}
								</Text>
								<span style={{ opacity: "0.6", whiteSpace: "nowrap" }}>
									{meta?.createdDateTime &&
										dayjs(meta.createdDateTime)?.format("D MMM YYYY")}
								</span>
							</div>
						</div>
					</div>
				</Spin>
			);
		});
	}, [
		files,
		fileMetaData,
		ownerList,
		containerId,
		tenantKey,
		api,
		canCollaborate,
	]);

	if (files.length === 0) {
		return null;
	}

	return (
		<div
			id="quick-access-container"
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "20px",
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "6px",
				}}
			>
				<span style={{ fontSize: "16px" }}>Quick Access</span>
				<Tooltip title="Showing recently added files">
					<span style={{ opacity: "0.5" }}>{GetAntIcon("question3")}</span>
				</Tooltip>
				<span onClick={onToggleCollapse} style={{ cursor: "pointer" }}>
					{collapsed ? GetAntIcon("downarrow") : GetAntIcon("up")}
				</span>
			</div>

			<div
				className={`quick-access-content ${
					collapsed ? "collapsed" : "expanded"
				} ${files.length > 0 ? "quick-access-margin" : ""}`}
				style={{
					display: collapsed ? "none" : "flex",
					overflowX: "scroll",
					gap: "15px",
				}}
			>
				{quickAccessItems}
			</div>
		</div>
	);
}

export default SharepointQuickAccess;
